<script setup lang="ts">
/**
 * AIAssistantStart.vue - Consent Modal
 * 
 * Standalone modal for user consent before starting the AI Assistant.
 * Shows privacy policy information and requires user acceptance.
 */
import { computed, ref, onMounted, onUnmounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { UserTracking } from "@/utils/UserTracking";

const debugLog = (location: string, message: string, data?: Record<string, unknown>) => {
  console.log(`[ConsentModal:${location}] ${message}`, data || "");
};

// =============================================================================
// Props & Emits
// =============================================================================

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  loadingStart: {
    type: Boolean,
    default: false
  },
  requireFirstConsent: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(["start", "close"]);

// =============================================================================
// State
// =============================================================================

const { locale, t } = useI18n();
const isLoading = ref(true);
const hasConsented = ref(false);
const loadingMessage = ref('Iniciando assistente...');
let messageInterval: ReturnType<typeof setInterval> | null = null;

// =============================================================================
// Computed
// =============================================================================

const imgUrl = computed(() => {
  return `${process.env.BASE_URL}assets/img/ai-assistant-start-${locale.value}.png?v=2`;
});

const privacy_terms_url =
  "https://dp-one-terms-conditions.s3.eu-central-1.amazonaws.com/BR+Master+T%26Cs+-+Doctor+Copilot+2024+.docx.pdf";

// =============================================================================
// Methods
// =============================================================================

const rotateLoadingMessage = () => {
  const messages = [
    'Iniciando assistente...',
    'Preparando modelo...',
    'Carregando informações...'
  ];
  let currentIndex = 0;

  messageInterval = setInterval(() => {
    currentIndex = (currentIndex + 1) % messages.length;
    loadingMessage.value = messages[currentIndex];
  }, 2000);
};

const handleClose = () => {
  debugLog("handleClose", `Close [isOpen=${props.isOpen}]`);
  emit("close");
};

const handleAccept = () => {
  debugLog("handleAccept", `Accept [hasConsented=${hasConsented.value}]`);
  
  if (!hasConsented.value) {
    localStorage.setItem('aiAssistant.hasConsented', 'true');
    hasConsented.value = true;
  }
  
  UserTracking.trackEvent("cta_clicked", {
    action: "initiate noa notes record",
    label: "accept terms and conditions",
    location: "medical episode",
    page_category: "patients",
    page_section: "patient profile",
    page_type: "",
  });
  
  emit("start");
  debugLog("handleAccept", "Start event emitted");
};

// =============================================================================
// Lifecycle
// =============================================================================

onMounted(() => {
  const storedConsent = localStorage.getItem('aiAssistant.hasConsented');
  hasConsented.value = storedConsent === 'true';
  isLoading.value = false;
  rotateLoadingMessage();
  debugLog("onMounted", `Mounted [hasConsented=${hasConsented.value}]`);
});

onUnmounted(() => {
  if (messageInterval) {
    clearInterval(messageInterval);
  }
});

// Watch for modal open state changes
watch(() => props.isOpen, (isOpen) => {
  debugLog("watch:isOpen", `Changed [${isOpen}]`);
});
</script>

<template>
  <w-modal
    :model-value="isOpen"
    @update:model-value="(val) => { if (!val) handleClose() }"
    size="medium"
    title="Termos de Uso"
    :close-on-click-outside="false"
    @close="handleClose"
  >
    <template #body>
      <div class="consent-content">
        <!-- Loading state -->
        <div v-if="loadingStart" class="loading-container" role="alert" aria-busy="true" aria-live="assertive">
          <div class="loading-content">
            <w-spinner size="small" :aria-label="t('startSection.loading')" />
            <w-text class="loading-message" type="body-bold">{{ loadingMessage }}</w-text>
          </div>
        </div>

        <!-- Consent content -->
        <w-spacer v-else all="large">
          <w-stack direction="column" gap="medium" justify-content="center">
            <w-text v-if="!hasConsented || requireFirstConsent">
              {{ $t("startSection.privacyPolicyTextStart") }}
              <w-link target="_blank" :href="privacy_terms_url">{{ $t("startSection.privacyPolicyLinkText") }}</w-link>
              {{ $t("startSection.privacyPolicyTextEnd") }} <br>
              {{ $t("startSection.privacyPolicyPatientConsent") }}
            </w-text>
            
            <w-stack direction="row" gap="small" justify-content="flex-end">
              <w-button
                label="Cancelar"
                variant="secondary"
                @click="handleClose"
              />
              <w-button
                :label="$t('startSection.startButtonLabel')"
                variant="primary"
                trailing-icon="arrow-right"
                :loading="loadingStart"
                @click="handleAccept"
              />
            </w-stack>
          </w-stack>
        </w-spacer>
      </div>
    </template>
  </w-modal>
</template>

<style scoped lang="scss">
@import "style-variables.scss";

.consent-content {
  position: relative;
  min-height: 150px;
  width: 100%;
  
  .w-button-primary {
    background-color: $primary-color;

    &:hover:not(.is-loading) {
      background-color: $primary-color-hover;
    }
  }
}

.loading-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 1;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-message {
  text-align: center;
  transition: opacity 0.3s ease;
  color: var(--w-color-foreground-primary);
  font-weight: 600;
  margin-top: 4px;
}
</style>
